import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/domains';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles
        .filter((a) => a.category === article.category && a.slug !== article.slug)
        .slice(0, 3)
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - ${siteInfo.name}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', article.excerpt);
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100">
              <i className="ri-error-warning-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel =
    article.category === 'entertainment' ? '娱乐圈' :
    article.category === 'social' ? '社会热点' :
    article.category === 'tech' ? '科技数码' :
    article.category === 'finance' ? '财经商业' :
    article.category === 'sports' ? '体育竞技' : article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: categoryLabel, to: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
                >
                  {categoryLabel}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                    热门
                  </span>
                )}
                {article.isNew && (
                  <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium whitespace-nowrap">
                    最新
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="whitespace-nowrap">
                  <i className="ri-user-line mr-1"></i>{article.author}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-time-line mr-1"></i>{article.publishDate}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>
                  {article.readCount > 10000
                    ? `${(article.readCount / 10000).toFixed(1)}万 阅读`
                    : `${article.readCount} 阅读`}
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-base max-w-none mb-10 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h3>/g, '<h3 class="font-heading text-xl text-foreground-900 mt-8 mb-3">')
                  .replace(/<p>/g, '<p class="text-foreground-700 leading-relaxed mb-4">'),
              }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-8 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                <i className="ri-price-tag-3-line mr-1"></i>标签：
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mb-10">
              <span className="text-sm text-foreground-500 whitespace-nowrap">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-link"></i>
              </button>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10 pt-10 border-t border-background-200/70">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">
                <i className="ri-article-line text-primary-500 mr-2"></i>相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}